import React from 'react';
import PropTypes from 'prop-types';
import { defaultTemplate } from 'core/hoc';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { BaasicFieldDropdown, BaasicButton, BaasicFormControls, BaasicConfirmModalNew, BaasicModal, BasicCheckbox } from 'core/components';

// ISG-2721 (part of ISG-2474): multi-DOS credit candidate list. Rendered inline here (rather than as a separate theme
// component) so it goes through the same proven modal wiring as the rest of this template.
// ISG-2722: the Status column shows Matched / Unmatched and the Notes column shows the differences (mismatch reasons),
// both from the backend candidate response. ISG-2723: checkbox selection and Confirm Credit; the pop-up opens after
// the status-change confirmation (Raizy 2026-09-16), and Confirm Credit applies the credit with no second confirmation.
function formatShortDate(d) {
    if (!d) return '';
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return '';
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    const yy = String(dt.getFullYear()).slice(-2);
    return `${mm}/${dd}/${yy}`;
}

const mdcLabelStyle = { fontSize: '11px', letterSpacing: '0.4px', textTransform: 'uppercase', color: '#8a94a6', marginBottom: '4px', whiteSpace: 'nowrap' };
const mdcValueStyle = { fontSize: '14px', fontWeight: 700, color: '#1f2d3d' };
// Table styling mirrors `.cand-table` from resources/Multi-Order-Credit-Prototype.html. The prototype's
// --isg-* custom properties aren't defined in this theme, so they're resolved to literals here; its `font`
// shorthand named Geist/Nunito Sans, which this app doesn't load, so the family is left to inherit ($ff-sans).
const mdcTableWrapStyle = { border: '1px solid #e5e9ed', borderRadius: '8px', overflow: 'hidden' };
const mdcTableStyle = { width: '100%', borderCollapse: 'collapse' };
const mdcThStyle = {
    textAlign: 'left',
    padding: '10px 12px',
    fontSize: '10px',
    lineHeight: '14px',
    fontWeight: 600,
    letterSpacing: '.06em',
    textTransform: 'uppercase',
    color: '#6b6f75',
    background: '#fcfcfc',
    borderBottom: '1px solid #e5e9ed',
    whiteSpace: 'nowrap'
};
const mdcTdStyle = { padding: '10px 12px', borderBottom: '1px solid #dee3e8', verticalAlign: 'middle' };
const mdcTdLastStyle = { ...mdcTdStyle, borderBottom: 'none' };
const mdcDosStyle = { fontSize: '13px', lineHeight: '18px', fontWeight: 500, color: '#0d131b' };
const mdcPsoStyle = { fontSize: '11px', lineHeight: '14px', fontWeight: 400, color: '#6b6f75' };
// ISG-2722: Status / Notes cell styling.
const mdcStatusMatchedStyle = { fontSize: '11px', lineHeight: '14px', fontWeight: 600, color: '#5cb85c', textTransform: 'uppercase' };
const mdcStatusUnmatchedStyle = { fontSize: '11px', lineHeight: '14px', fontWeight: 600, color: '#f0a04b', textTransform: 'uppercase' };
const mdcNotesStyle = { fontSize: '12px', lineHeight: '16px', color: '#6b6f75', whiteSpace: 'normal' };

// ISG-3042: BaasicModal clones its child with a `modalParams` prop, which React rejects on a plain DOM element.
// This wrapper takes that prop (and supplies the modal's single root div) so the inline pop-up content can stay in
// this template's (reactive) render.
function MultiDosCreditModalBody({ children }) {
    return <div>{children}</div>;
}

MultiDosCreditModalBody.propTypes = {
    children: PropTypes.node
};

function ChangeClinicalStatusModalTemplate({ changeClinicalStatusModalStore, t }) {
    const {
        form,
        patientTemplate,
        patientName,
        clinicalStatusStore,
        clinicalStatusWarningModal,
        clinicalStatusErrorModal,
        multiDosCreditCandidatesModal,
        viewAllCandidates,
        viewUnmatchedCandidates,
        viewCandidate,
        toggleMultiDosCandidate,
        modalParams
    } = changeClinicalStatusModalStore;

    const currentStatusName = (patientTemplate.clinicalStatus && patientTemplate.clinicalStatus.name) || 'Unselected';
    const templateCategory = (patientTemplate.subCategory && patientTemplate.subCategory.name) || '';

    const mdcData = (multiDosCreditCandidatesModal && multiDosCreditCandidatesModal.data) || {};
    const mdcCandidates = mdcData.candidates || [];
    // ISG-2727: nothing to open when every listed candidate matches the source PSO.
    const mdcHasUnmatched = mdcCandidates.some(c => !c.isMatched);
    // ISG-2723: Confirm is greyed out until every Unmatched PSO has been opened, so the rep has to acknowledge them
    // before crediting. Reading these off the store (not a local copy) keeps the button reactive.
    const mdcConfirmLocked = changeClinicalStatusModalStore.multiDosConfirmLocked;
    const mdcApplying = changeClinicalStatusModalStore.multiDosApplying;

    return (
        <div>
            <div className="modal__body">
                <div className="type--center u-mar--bottom--med">
                    <i style={{ cursor: "default" }} className="status--sqare modal-sync u-mar--bottom--med u-radius--full"></i>
                    <h3 className="modal__header__title type--xlrg">Change Clinical Status</h3>
                </div>
                <div className="u-mar--bottom--sml">
                    <span className="type--med">Patient: </span>
                    <span className="type--med type--wgt--bold">{patientName}</span>
                </div>
                <div className="u-mar--bottom--sml">
                    <span className="type--med">Template Category: </span>
                    <span className="type--med type--wgt--bold">{templateCategory}</span>
                </div>
                <div className="u-mar--bottom--med">
                    <span className="type--med">Current Status: </span>
                    <span className="type--med type--wgt--bold">{currentStatusName}</span>
                </div>
                <div className="form__group">
                    <label className="form__group__label">New Status:</label>
                    <BaasicFieldDropdown field={form.$('clinicalStatusId')} store={clinicalStatusStore} allowEmpty={true} labelDisplayed={false} disabled={form.submitting || form.validating} />
                </div>
            </div>
            <div className="modal__footer is-center">
                <BaasicButton
                    disabled={form.submitting || form.validating}
                    className="btn btn--med btn--light"
                    label={t('MODAL.CANCEL_LABEL')}
                    onClick={() => modalParams.close()}
                />
                <BaasicFormControls
                    className="btn btn--med btn--primary"
                    form={form}
                    onSubmit={form.onSubmit}
                    disabled={form.submitting || form.validating}
                    label="Submit Change"
                />
            </div>
            <BaasicConfirmModalNew modalParams={clinicalStatusWarningModal} />
            <BaasicConfirmModalNew modalParams={clinicalStatusErrorModal} />

            <BaasicModal modalParams={multiDosCreditCandidatesModal} className="modal__content modal--lrg">
                <MultiDosCreditModalBody>
                    <div className="modal__body" style={{ paddingLeft: '16px', paddingRight: '16px' }}>
                        <div className="type--center u-mar--bottom--med">
                            <i style={{ cursor: 'default' }} className="status--sqare modal-copy-2 u-mar--bottom--sml u-radius--full"></i>
                            <h3 className="modal__header__title type--xlrg">Apply Credit to Other Orders</h3>
                        </div>

                        <div style={{ display: 'flex', gap: '48px', padding: '0 4px', marginBottom: '20px' }}>
                            <div>
                                <div style={mdcLabelStyle}>Patient</div>
                                <div style={mdcValueStyle}>{mdcData.patientName || ''}</div>
                            </div>
                            <div>
                                <div style={mdcLabelStyle}>Template Category</div>
                                <div style={mdcValueStyle}>{mdcData.templateCategory || ''}</div>
                            </div>
                            <div>
                                <div style={mdcLabelStyle}>Source DOS</div>
                                <div style={mdcValueStyle}>{formatShortDate(mdcData.sourceDos)}</div>
                            </div>
                            <div>
                                <div style={mdcLabelStyle}>Source Invoice</div>
                                <div style={mdcValueStyle}>{mdcData.sourceInvoiceNumber || ''}</div>
                            </div>
                            <div>
                                <div style={mdcLabelStyle}>New Status</div>
                                <div style={mdcValueStyle}>{mdcData.newStatus || ''}</div>
                            </div>
                        </div>

                        <div style={mdcTableWrapStyle}>
                            <table style={mdcTableStyle}>
                                <colgroup>
                                    <col style={{ width: '44px' }} />
                                    <col style={{ width: '150px' }} />
                                    <col style={{ width: '120px' }} />
                                    <col />
                                    <col style={{ width: '90px' }} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th style={mdcThStyle}></th>
                                        <th style={mdcThStyle}>DOS</th>
                                        <th style={mdcThStyle}>Status</th>
                                        <th style={mdcThStyle}>Notes</th>
                                        <th style={mdcThStyle}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {mdcCandidates.map((c, idx) => {
                                        const tdStyle = idx === mdcCandidates.length - 1 ? mdcTdLastStyle : mdcTdStyle;
                                        return (
                                            <tr key={c.id} style={{ background: '#ffffff' }}>
                                                {/* ISG-2723: the rep picks which PSOs are credited. All rows start checked
                                                    when every candidate matches; a single Unmatched candidate leaves them
                                                    all unchecked (isSelectedByDefault, decided by the backend). */}
                                                <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                    {/* The styled checkbox is drawn on the sibling label (.input--check is
                                                        itself hidden), so this goes through BasicCheckbox rather than a
                                                        bare input; --no-label is the variant used for table cells.
                                                        has-fake-label zeroes the check glyph's top offset to match the
                                                        box, which --no-label alone leaves 3px lower outside a k-grid. */}
                                                    <BasicCheckbox
                                                        id={`mdc-candidate-${c.id}`}
                                                        className="input--check input--check--no-label has-fake-label"
                                                        label=""
                                                        checked={changeClinicalStatusModalStore.isMultiDosCandidateSelected(c.id)}
                                                        disabled={mdcApplying}
                                                        onChange={() => toggleMultiDosCandidate(c.id)}
                                                    />
                                                </td>
                                                <td style={tdStyle}>
                                                    <div style={mdcDosStyle}>{formatShortDate(c.dos)}</div>
                                                    {/* ISG-2721 (Raizy 2026-08-17): identify the candidate by the number of
                                                        the invoice that will be credited, with no "PSO" prefix. Nothing is
                                                        shown when the candidate has no linked invoice. */}
                                                    {c.invoiceNumber ? <div style={mdcPsoStyle}>{c.invoiceNumber}</div> : null}
                                                </td>
                                                <td style={tdStyle}>
                                                    <span style={c.isMatched ? mdcStatusMatchedStyle : mdcStatusUnmatchedStyle}>{c.status}</span>
                                                </td>
                                                <td style={tdStyle}>
                                                    {/* ISG-2722 / ISG-2875: every reason on one line, separated by an interpunct per the design. */}
                                                    <span style={mdcNotesStyle}>{(c.mismatchReasons || []).join(' · ')}</span>
                                                </td>
                                                {/* ISG-2727: opens the PSO page in a new tab filtered to this PSO only. */}
                                                <td style={{ ...tdStyle, textAlign: 'right' }}>
                                                    <BaasicButton
                                                        className="btn btn--link"
                                                        icon="u-icon u-icon--med u-icon--expand--grey"
                                                        label="View"
                                                        onlyIcon={true}
                                                        onClick={() => viewCandidate(c)}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* ISG-2727: open the PSO page in a new tab for every listed PSO, or only the Unmatched ones.
                            Sits inside the body so it stays tucked directly under the table, above the action buttons. */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '8px' }}>
                            <BaasicButton
                                className="btn btn--link btn--link--underline type--wgt--regular type--sml"
                                label="View All"
                                onClick={viewAllCandidates}
                            />
                            <BaasicButton
                                className="btn btn--link btn--link--underline type--wgt--regular type--sml"
                                label="View All Unmatched"
                                disabled={!mdcHasUnmatched}
                                onClick={viewUnmatchedCandidates}
                            />
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', padding: '16px' }}>
                        <BaasicButton
                            className="btn btn--med btn--light"
                            label={t('MODAL.CANCEL_LABEL')}
                            disabled={mdcApplying}
                            onClick={multiDosCreditCandidatesModal.onCancel}
                        />
                        {/* ISG-2723: locked until every Unmatched PSO has been opened, with the tooltip explaining why.
                            Three things are needed for that tooltip to actually appear:
                            - a plain button, not BaasicButton, which always writes its own label into the button's title
                              and would show "Confirm Credit" instead;
                            - the trigger on the wrapping span, because a disabled control fires no hover events at all
                              (Firefox) and react-tooltip would never be told to open;
                            - pointer-events: none on the locked button so the pointer reaches that span.
                            Content is undefined once unlocked, which is how react-tooltip is told to stay closed. */}
                        <ReactTooltip positionStrategy="fixed" id="multiDosConfirmTip" place="top" />
                        <span
                            data-tooltip-id="multiDosConfirmTip"
                            data-tooltip-content={mdcConfirmLocked ? 'Review all unmatched orders to continue.' : undefined}
                        >
                            <button
                                type="button"
                                className="btn btn--med btn--primary"
                                disabled={mdcConfirmLocked || mdcApplying}
                                style={mdcConfirmLocked ? { pointerEvents: 'none' } : undefined}
                                onClick={multiDosCreditCandidatesModal.onConfirm}
                            >
                                {/* Same spinner the confirm pop-ups show on their Confirm button while a save runs, so the
                                    rep can see the credit is in progress; the button is disabled for its duration. */}
                                {mdcApplying && <i className="u-icon u-icon--xmed u-icon--sync u-rotate--login u-mar--right--tny"></i>}
                                Confirm Credit
                            </button>
                        </span>
                    </div>
                </MultiDosCreditModalBody>
            </BaasicModal>
        </div>
    );
}

ChangeClinicalStatusModalTemplate.propTypes = {
    changeClinicalStatusModalStore: PropTypes.object,
    t: PropTypes.func
};

export default defaultTemplate(ChangeClinicalStatusModalTemplate);
